import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Briefcase,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Badge } from "../components/Badge";
import { JobCompetencySection } from "../components/JobCompetencySection";
import { PageHeader } from "../components/ui/PageHeader";
import { ErrorPanel, Loading } from "../components/Loading";
import { AIGenerateButton } from "../components/ui/AIGenerateButton";
import { EditableField } from "../components/ui/EditableField";
import { EntityDrawer } from "../components/ui/EntityDrawer";
import { api, ApiError } from "../lib/api";
import type {
  AiJobFullProfile,
  JobCompetency,
  JobSummary,
  JobUpdateBody,
} from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

type CompetencyKind = "skill" | "kpi" | "certificate" | "test" | "objective";

const _KIND_LABEL_KEYS: Record<CompetencyKind, string> = {
  skill: "job.section_skills",
  kpi: "job.section_kpis",
  certificate: "job.section_certificates",
  test: "job.section_tests",
  objective: "job.section_objectives",
};

/**
 * Job detail page. Shows the full Job record with inline-edit on every
 * mutable field, the five nested competency collections (Skills /
 * KPIs / Certificates / Tests / Objectives), an AI-generate button
 * for the job description, and a "Positions using this Job" panel.
 */
export function JobDetailPage() {
  const { t } = useTranslation();
  const { jobId } = useParams<{ jobId: string }>();
  const le = useLegalEntityId();
  const qc = useQueryClient();
  const [generating, setGenerating] = useState(false);

  const jobQ = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => api.getJob(jobId!),
    enabled: !!jobId,
  });

  const positionsQ = useQuery({
    queryKey: ["positions", le, "job", jobId],
    queryFn: () => api.listPositions(le, { jobId }),
    enabled: !!le && !!jobId,
  });

  if (jobQ.isLoading) return <Loading />;
  if (jobQ.error) return <ErrorPanel message={(jobQ.error as Error).message} />;
  if (!jobQ.data)
    return <ErrorPanel message={t("job_detail.job_not_found")} />;

  const job = jobQ.data;
  const positions = positionsQ.data ?? [];

  const patchJob = (patch: JobUpdateBody) =>
    api.updateJob(job.id, patch).then((updated) => {
      qc.setQueryData(["job", job.id], updated);
      qc.invalidateQueries({ queryKey: ["jobs"] });
      return updated;
    });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${job.code} · ${job.title_en}`}
        breadcrumbs={[
          { label: t("job_detail.back_to_jobs"), href: "/people/jobs" },
          { label: job.code },
        ]}
        actions={
          <button
            type="button"
            onClick={() => setGenerating(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-ai-accent/40 bg-ai-bg px-3 py-1.5 text-xs font-medium text-ai-deep hover:opacity-90"
          >
            <Sparkles size={12} aria-hidden />
            {t("job.ai_generate_full_profile")}
          </button>
        }
      />

      {/* Core fields */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("job_detail.section_template")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("job_detail.title_en")}
            value={job.title_en}
            onSave={(v) => patchJob({ title_en: (v as string) ?? "" })}
          />
          <EditableField
            label={t("job_detail.title_ar")}
            value={job.title_ar}
            rtl
            onSave={(v) => patchJob({ title_ar: (v as string) ?? "" })}
          />
          <EditableField
            label={t("job_detail.description")}
            value={job.description}
            kind="textarea"
            onSave={(v) => patchJob({ description: v as string | null })}
          />
          <EditableField
            label={t("job_detail.job_family")}
            value={job.job_family}
            onSave={(v) => patchJob({ job_family: v as string | null })}
          />
          <EditableField
            label={t("job_detail.mol_number")}
            value={job.mol_number}
            onSave={(v) => patchJob({ mol_number: v as string | null })}
          />
          <EditableField
            label={t("job_detail.function_code")}
            value={job.function_code}
            onSave={(v) => patchJob({ function_code: v as string | null })}
          />
          <EditableField
            label={t("job_detail.sector")}
            value={job.sector_type}
            kind="select"
            options={[
              { value: "private", label: t("job_detail.sector_private") },
              { value: "public", label: t("job_detail.sector_public") },
              { value: "mixed", label: t("job_detail.sector_mixed") },
            ]}
            onSave={(v) => patchJob({ sector_type: (v as string) || "private" })}
          />
          <EditableField
            label={t("job_detail.job_type")}
            value={job.job_type}
            kind="select"
            options={[
              { value: "full_time", label: t("job_detail.type_full_time") },
              { value: "part_time", label: t("job_detail.type_part_time") },
              { value: "contract", label: t("job_detail.type_contract") },
              { value: "temporary", label: t("job_detail.type_temporary") },
            ]}
            onSave={(v) => patchJob({ job_type: (v as string) || "full_time" })}
          />
          <EditableField
            label={t("job_detail.fte")}
            value={job.full_time_equivalent}
            kind="number"
            onSave={(v) =>
              patchJob({ full_time_equivalent: v == null ? undefined : String(v) })
            }
          />
          <EditableField
            label={t("job_detail.hours_per_week")}
            value={job.working_hours_per_week}
            kind="number"
            onSave={(v) =>
              patchJob({ working_hours_per_week: v == null ? undefined : String(v) })
            }
          />
          <EditableField
            label={t("job_detail.max_positions")}
            value={job.max_positions}
            kind="number"
            onSave={(v) =>
              patchJob({ max_positions: v == null ? null : Number(v) })
            }
          />
          <EditableField
            label={t("job_detail.supervisory_role")}
            value={job.is_supervisory}
            kind="boolean"
            onSave={(v) => patchJob({ is_supervisory: Boolean(v) })}
          />
        </div>

        {/* Job description — inline-editable, with an AI-generate
            helper (commandment 23: AIGenerateButton returns a DRAFT
            HR reviews + accepts; never written straight to live). */}
        <div className="mt-4 border-t border-slate-100 pt-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("job_detail.job_description_header")}
            </span>
            <AIGenerateButton
              kind="job_description"
              legalEntityId={le}
              context={{
                job_title: job.title_en,
                job_family: job.job_family ?? "General",
                context: "a Saudi Arabian enterprise",
              }}
              onAccept={(draft) => void patchJob({ description: draft })}
            />
          </div>
          <div className="mt-2">
            <EditableField
              label={t("job_detail.description")}
              value={job.description}
              kind="textarea"
              onSave={(v) =>
                patchJob({ description: v == null ? null : String(v) })
              }
            />
          </div>
        </div>
      </section>

      {/* Five nested competency collections (M21.7) */}
      <JobCompetencySection jobId={job.id} kind="skill" />
      <JobCompetencySection jobId={job.id} kind="kpi" />
      <JobCompetencySection jobId={job.id} kind="certificate" />
      <JobCompetencySection jobId={job.id} kind="test" />
      <JobCompetencySection jobId={job.id} kind="objective" />

      {/* Used by */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          <Briefcase className="h-4 w-4" />
          {t("job_detail.positions_using_this_job", { count: positions.length })}
        </h2>
        {positions.length === 0 ? (
          <p className="text-sm italic text-slate-500">
            {t("job_detail.no_positions_using_job")}
          </p>
        ) : (
          <table className="min-w-full divide-y divide-slate-100">
            <thead>
              <tr className="text-start text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-2 py-2 text-start">{t("job_detail.col_number")}</th>
                <th className="px-2 py-2 text-start">{t("job_detail.col_title")}</th>
                <th className="px-2 py-2 text-start">{t("job_detail.col_department")}</th>
                <th className="px-2 py-2 text-start">{t("job_detail.col_status")}</th>
                <th className="px-2 py-2 text-start">{t("job_detail.col_worker")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {positions.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-2 py-2 font-mono text-sm tabular-nums">
                    <Link
                      to={`/people/positions/${p.id}`}
                      className="hover:underline"
                    >
                      {p.position_number_display}
                    </Link>
                  </td>
                  <td className="px-2 py-2 text-sm">
                    <Link
                      to={`/people/positions/${p.id}`}
                      className="hover:underline"
                    >
                      {p.title_en}
                    </Link>
                  </td>
                  <td className="px-2 py-2 text-sm text-slate-700">
                    {p.department_name_en}
                  </td>
                  <td className="px-2 py-2 text-sm">
                    <Badge tone={p.status === "filled" ? "green" : "slate"}>
                      {p.status}
                    </Badge>
                  </td>
                  <td className="px-2 py-2 text-sm text-slate-700">
                    {p.current_assignee
                      ? `${p.current_assignee.first_name_en} ${p.current_assignee.last_name_en}`
                      : <span className="italic text-slate-400">{t("job_detail.vacant")}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {generating && (
        <GenerateFullProfileDrawer
          job={job}
          legalEntityId={le}
          onClose={() => setGenerating(false)}
          onApplied={() => {
            qc.invalidateQueries({ queryKey: ["job", job.id] });
            qc.invalidateQueries({ queryKey: ["job-competencies"] });
            setGenerating(false);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// "Generate full profile" drawer — calls the AI endpoint, shows
// a review preview with per-section Append/Replace toggle, then
// on Apply:
//   - PATCH description (always REPLACES the single description field)
//   - For each of 5 competency kinds:
//       * append mode: just POST each new item
//       * replace mode: LIST existing rows for the kind, DELETE
//         each, then POST the new ones
// ============================================================================

type SectionMode = "append" | "replace";

function GenerateFullProfileDrawer({
  job,
  legalEntityId,
  onClose,
  onApplied,
}: {
  job: JobSummary;
  legalEntityId: string | null | undefined;
  onClose: () => void;
  onApplied: () => void;
}) {
  const { t } = useTranslation();
  // Default each kind to append (the safe default per Amer's
  // explicit clarification).
  const [modes, setModes] = useState<Record<CompetencyKind, SectionMode>>({
    skill: "append",
    kpi: "append",
    certificate: "append",
    test: "append",
    objective: "append",
  });
  const [progress, setProgress] = useState<{
    written: number;
    total: number;
    errors: string[];
  } | null>(null);

  // Generate immediately on mount.
  const gen = useMutation({
    mutationFn: () => {
      if (!legalEntityId) {
        return Promise.reject(
          new Error("No legal entity in context for this job."),
        );
      }
      return api.aiJobFullProfile({
        title_en: job.title_en,
        legal_entity_id: legalEntityId,
      });
    },
  });
  // Fire the generation when the drawer opens — useState
  // initializer can't return a promise, so we rely on react-query
  // + a no-arg trigger.
  if (gen.isIdle) {
    gen.mutate();
  }

  const profile = gen.data;

  const counts = profile
    ? {
        skill: profile.skills.length,
        kpi: profile.kpis.length,
        certificate: profile.certificates.length,
        test: profile.tests.length,
        objective: profile.objectives.length,
      }
    : null;
  const totalCompetencies = counts
    ? Object.values(counts).reduce((a, b) => a + b, 0)
    : 0;

  // Per-kind items list — used to drive the apply loop.
  const itemsForKind = (
    p: AiJobFullProfile,
    k: CompetencyKind,
  ): Array<Record<string, unknown>> => {
    if (k === "skill") return p.skills as unknown as Array<Record<string, unknown>>;
    if (k === "kpi") return p.kpis as unknown as Array<Record<string, unknown>>;
    if (k === "certificate")
      return p.certificates as unknown as Array<Record<string, unknown>>;
    if (k === "test") return p.tests as unknown as Array<Record<string, unknown>>;
    return p.objectives as unknown as Array<Record<string, unknown>>;
  };

  const apply = useMutation({
    mutationFn: async () => {
      if (!profile) return { written: 0, total: 0, errors: [] };

      // 1. Description — always REPLACE (one field).
      await api.updateJob(job.id, { description: profile.description });

      // 2. For each kind, replace OR append per the user's mode.
      const kinds: CompetencyKind[] = [
        "skill",
        "kpi",
        "certificate",
        "test",
        "objective",
      ];

      let written = 0;
      const errors: string[] = [];
      const total = totalCompetencies;

      for (const kind of kinds) {
        if (modes[kind] === "replace") {
          // Delete existing competencies of this kind.
          try {
            const existing: JobCompetency[] = await api.listJobCompetencies(
              job.id,
              kind,
            );
            for (const ex of existing) {
              try {
                await api.deleteJobCompetency(ex.id);
              } catch (e) {
                const msg =
                  e instanceof ApiError
                    ? e.detail
                    : String((e as Error).message);
                errors.push(`${kind} delete: ${msg}`);
              }
            }
          } catch (e) {
            const msg =
              e instanceof ApiError ? e.detail : String((e as Error).message);
            errors.push(`${kind} list: ${msg}`);
          }
        }

        // Insert AI items.
        const items = itemsForKind(profile, kind);
        for (let i = 0; i < items.length; i++) {
          const body = items[i];
          try {
            await api.createJobCompetency(job.id, {
              kind,
              ...(body as { name_en: string }),
              sequence_index: i,
            });
            written += 1;
            setProgress({ written, total, errors });
          } catch (e) {
            const msg =
              e instanceof ApiError ? e.detail : String((e as Error).message);
            errors.push(`${kind}: ${msg}`);
          }
        }
      }

      return { written, total, errors };
    },
    onSuccess: (result) => {
      if (result.errors.length === 0) {
        onApplied();
      } else {
        setProgress(result);
      }
    },
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("job_detail.generate_drawer_title")}
      description={t("job_detail.generate_drawer_description")}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            {t("job_detail.cancel")}
          </button>
          <button
            type="button"
            disabled={!profile || apply.isPending}
            onClick={() => {
              setProgress(null);
              apply.mutate();
            }}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            {apply.isPending
              ? progress
                ? t("job_detail.applying_progress", {
                    written: progress.written,
                    total: progress.total,
                  })
                : t("job_detail.applying")
              : t("job_detail.apply_to_job")}
          </button>
        </div>
      }
    >
      {gen.isPending && (
        <div className="flex items-center gap-2 rounded-md border border-ai-subtle bg-ai-bg p-3 text-sm text-ai-deep">
          <Sparkles size={14} aria-hidden />{" "}
          {t("job_detail.generating_profile_from", { title: job.title_en })}
        </div>
      )}
      {gen.error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700"
        >
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          {(gen.error as Error).message}
        </div>
      )}
      {profile && (
        <div className="flex flex-col gap-3 text-sm">
          {/* Description preview */}
          <section className="rounded-md border border-border-subtle bg-surface-default p-3">
            <header className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              {t("job_detail.desc_replace_existing")}
            </header>
            <p className="whitespace-pre-wrap text-sm text-text-primary">
              {profile.description}
            </p>
          </section>

          {/* Each competency section with mode toggle */}
          {(
            ["skill", "kpi", "certificate", "test", "objective"] as const
          ).map((kind) => (
            <SectionRow
              key={kind}
              kind={kind}
              items={itemsForKind(profile, kind)}
              mode={modes[kind]}
              onModeChange={(m) =>
                setModes((prev) => ({ ...prev, [kind]: m }))
              }
            />
          ))}

          {progress && progress.errors.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle size={12} aria-hidden />
                {t("job_detail.applied_partial", {
                  written: progress.written,
                  total: progress.total,
                })}
              </div>
              <ul className="mt-1 list-disc ps-5">
                {progress.errors.slice(0, 5).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
          {apply.isSuccess &&
            progress &&
            progress.errors.length === 0 && (
              <div className="flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
                <CheckCircle2 size={12} aria-hidden />
                {t("job_detail.applied_success", { written: progress.written })}
              </div>
            )}
        </div>
      )}
    </EntityDrawer>
  );
}

function SectionRow({
  kind,
  items,
  mode,
  onModeChange,
}: {
  kind: CompetencyKind;
  items: Array<Record<string, unknown>>;
  mode: SectionMode;
  onModeChange: (m: SectionMode) => void;
}) {
  const { t } = useTranslation();
  const kindLabel = t(_KIND_LABEL_KEYS[kind]);
  return (
    <section className="rounded-md border border-border-subtle bg-surface-default p-3">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-blue px-1.5 text-[11px] font-semibold text-white">
            {items.length}
          </span>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-primary">
            {kindLabel}
          </h3>
        </div>
        <fieldset className="flex items-center gap-3 text-xs">
          <legend className="sr-only">
            {t("job_detail.how_to_apply_label", { kind: kindLabel })}
          </legend>
          <label className="inline-flex items-center gap-1">
            <input
              type="radio"
              name={`mode-${kind}`}
              checked={mode === "append"}
              onChange={() => onModeChange("append")}
            />
            <span className="text-text-secondary">{t("job_detail.mode_append")}</span>
          </label>
          <label className="inline-flex items-center gap-1">
            <input
              type="radio"
              name={`mode-${kind}`}
              checked={mode === "replace"}
              onChange={() => onModeChange("replace")}
            />
            <span className="text-amber-700">{t("job_detail.mode_replace")}</span>
          </label>
        </fieldset>
      </header>
      {items.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs">
          {items.slice(0, 8).map((it, i) => (
            <li key={i} className="flex items-baseline gap-2">
              <span className="font-medium text-text-primary">
                {String(it.name_en ?? "")}
              </span>
              {(it.description as string | undefined) && (
                <span className="text-text-tertiary">
                  — {String(it.description)}
                </span>
              )}
            </li>
          ))}
          {items.length > 8 && (
            <li className="text-text-tertiary">
              {t("job_detail.more_items", { count: items.length - 8 })}
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
